import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/lingo/DashboardLayout";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ROLES, PERM_LABELS, hasPerm } from "@/lib/permissions";
import { Search, ShieldCheck, ChevronDown } from "lucide-react";
import { toast } from "sonner";

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("");
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    const params = {};
    if (filter) params.role = filter;
    if (search) params.search = search;
    const [u, r] = await Promise.all([
      api.get("/admin/users", { params }),
      api.get("/admin/roles"),
    ]);
    setUsers(u.data);
    setRoles(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const changeRole = async (id, role) => {
    try {
      await api.patch(`/admin/users/${id}/role`, { role });
      toast.success("Role updated");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const canManage = hasPerm(me, "user:manage");

  return (
    <DashboardLayout title="User Management">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="users-search" value={search} onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search by name or email..."
            className="w-full pl-10 bg-[#F6F8FB]/50 border border-slate-200 rounded-xl px-4 py-2.5" />
        </div>
        <select data-testid="users-filter" value={filter} onChange={(e) => setFilter(e.target.value)}
          className="bg-[#F6F8FB]/50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm">
          <option value="">All roles</option>
          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <button onClick={load} data-testid="users-refresh" className="px-4 py-2.5 rounded-xl bg-slate-100 border border-slate-200 text-sm">Refresh</button>
        <div className="ml-auto text-xs text-slate-400">{users.length} users</div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="grid grid-cols-12 gap-3 px-5 py-3 text-xs uppercase tracking-widest text-slate-400 border-b border-slate-200">
          <div className="col-span-4">User</div>
          <div className="col-span-3">Role</div>
          <div className="col-span-3">Test</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {users.map((u) => (
          <div key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-slate-100">
            <div className="grid grid-cols-12 gap-3 px-5 py-4 items-center">
              <div className="col-span-4 flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#003049] to-[#0C4B66] flex items-center justify-center text-sm shrink-0">{u.name?.[0]}</div>
                <div className="min-w-0">
                  <div className="text-sm truncate">{u.name}</div>
                  <div className="text-xs text-slate-400 truncate">{u.email}</div>
                </div>
              </div>
              <div className="col-span-3">
                {canManage ? (
                  <select data-testid={`role-select-${u.id}`} value={u.role} onChange={(e) => changeRole(u.id, e.target.value)}
                    className="bg-[#F6F8FB]/50 border border-slate-200 rounded-lg px-3 py-1.5 text-xs">
                    {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                ) : (
                  <span className="text-xs px-3 py-1 rounded-full border border-slate-200">{u.role}</span>
                )}
              </div>
              <div className="col-span-3 text-xs text-slate-500">{u.test_type?.replaceAll("_", " ") || "—"}</div>
              <div className="col-span-2 text-right">
                <button onClick={() => setExpanded(expanded === u.id ? null : u.id)} data-testid={`user-expand-${u.id}`}
                  className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
                  Permissions <ChevronDown className={`w-3 h-3 transition-transform ${expanded === u.id ? "rotate-180" : ""}`} />
                </button>
              </div>
            </div>
            {expanded === u.id && (
              <div className="px-5 pb-5">
                <div className="flex items-center gap-2 text-xs text-slate-400 mb-2"><ShieldCheck className="w-3 h-3" /> Permissions for {u.role}</div>
                <div className="flex flex-wrap gap-1.5">
                  {(roles.find((r) => r.role === u.role)?.permissions || []).map((p) => (
                    <span key={p} className="px-2.5 py-1 rounded-full bg-slate-50 border border-slate-200 text-[11px] font-mono">{p}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
        {!users.length && <div className="px-5 py-10 text-center text-slate-400">No users match.</div>}
      </div>

      <div className="mt-8 bg-white border border-slate-200 rounded-2xl p-6">
        <div className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-3">Role permission matrix</div>
        <div className="overflow-x-auto">
          <table className="text-xs w-full min-w-[700px]">
            <thead>
              <tr className="text-slate-400">
                <th className="text-left py-2 pr-3">Permission</th>
                {ROLES.map((r) => <th key={r} className="px-2 py-2">{r.replace("_", " ")}</th>)}
              </tr>
            </thead>
            <tbody>
              {Object.entries(PERM_LABELS).map(([p, label]) => (
                <tr key={p} className="border-t border-slate-100">
                  <td className="py-2 pr-3 text-slate-600">{label} <span className="text-slate-300 font-mono">{p}</span></td>
                  {ROLES.map((r) => {
                    const has = (roles.find((x) => x.role === r)?.permissions || []).includes(p);
                    return <td key={r} className="px-2 py-2 text-center">{has ? <span className="text-[#16A34A]">●</span> : <span className="text-slate-200">○</span>}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  );
}
